import { RestUseCase, ServiceOperationResult } from 'service/api/rest/rest-usecase';
import { FluxoAprovacaoPedidoEntity } from '../../../@types/entity/fluxo-aprovacao-pedido-entity';
import { PedidoDetalhe } from '../../../@types/entity/pedido-detalhe-entity';
import { PedidoResumo } from '../../../@types/entity/pedido-resumo-entity';
import { PedidoVersaoDetalhe } from '../../../@types/entity/pedido-versao-detalhe-entity';
import { ConsultaAprovador, ConsultaAprovadorFiltro } from '../../../@types/entity/consulta-aprovador-entity';

export function queryStringConsultaAprovadores(filtro: ConsultaAprovadorFiltro, page: number, size: number): string {
  const params = new URLSearchParams();
  (filtro.idFilial ?? []).forEach((id) => params.append('idFilial', String(id)));
  (filtro.idAprovador ?? []).forEach((id) => params.append('idAprovador', String(id)));
  if (filtro.estado && filtro.estado !== 'TODOS') {
    params.set('estado', filtro.estado);
  }
  params.set('page', String(page));
  params.set('size', String(size));
  return `?${params.toString()}`;
}

/** Tipos de solicitacao item-a-item da acao "Editar" (§4.5 / RN12). */
export type SolicitacaoTipo = 'REMOCAO' | 'ADICAO' | 'ALTERACAO_QTD';

/**
 * Solicitacao item-a-item enviada pela acao "Editar" (§4.5). Para ADICAO/ALTERACAO_QTD
 * a `quantidade` (nova) e obrigatoria; para ADICAO o `valor` e resolvido no backend pela filial.
 */
export interface SolicitacaoItem {
  tipo: SolicitacaoTipo;
  idItem: number;
  quantidade?: number;
  valor?: number;
}

/**
 * Item retornado pela busca (typeahead) de itens disponiveis da filial do pedido.
 * Espelha ItemDisponivelDTO da API: idItem (codigo usado na grade), codigo do fabricante,
 * descricao (best-effort), valor sugerido e a quantidade sugerida do item,
 * usada para pre-preencher o input de quantidade ao adicionar o item.
 */
export interface ItemBusca {
  idItem: number;
  codigo?: string;
  descricao?: string;
  valor?: number;
  quantidadeSugerida?: number;
  /** Mesmas colunas da visualização do item, quando a busca as devolve. */
  qme?: number;
  precoReposicao?: number;
  custoMedio?: number;
  precoPedido?: number;
  cvf?: string;
  ncvf?: string;
  pontuacao?: number;
  mediaDemanda?: number;
  demandaMesAtual?: number;
  demandaMes1?: number;
  demandaMes2?: number;
  demandaMes3?: number;
  demandaMes4?: number;
  demandaMes5?: number;
  estoque?: number;
  bloqueadas?: number;
  pendentes?: number;
  precoSemSt?: number;
  precoSemStDesconto?: number;
  precoComSt?: number;
  totalizadorPrecoSemSt?: number;
  totalizadorPrecoSemStDesconto?: number;
  totalizadorPrecoComSt?: number;
}

/**
 * Service do novo fluxo de aprovacao de pedido, tela do operador.
 * Reaproveita RestUseCase.Post_, que normaliza a resposta em ServiceOperationResult
 * (inclusive o statusCode, permitindo tratar o bloqueio HTTP 409).
 */
export class FluxoAprovacaoPedidoUseCase {
  constructor(private readonly baseEndpoint: string) {
    this.baseEndpoint = baseEndpoint;
  }

  /**
   * Origina os pedidos a partir da selecao ja persistida do operador no Analitico
   * (mesma fonte do fluxo legado). Como no legado, envia apenas o nome do pedido; a API
   * le os itens alterados, agrupa por filial e cria um pedido por filial ja enviado para
   * aprovacao. Retorna a lista de pedidos originados.
   */
  async originarDoAnalitico(orderName: string | null): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity[]>> {
    return new RestUseCase(`${this.baseEndpoint}/originar-analitico`).Post_<FluxoAprovacaoPedidoEntity[]>({
      data: { orderName },
    });
  }

  /**
   * Origina os pedidos a partir da selecao ja persistida do operador no Sintetico (FU01).
   * Mesma mecanica do Analitico: a API le os itens alterados, agrupa por filial e cria um pedido
   * por filial ja enviado para aprovacao. Retorna a lista de pedidos originados.
   *
   * `idFilial` espelha o comportamento legado (modal "Selecione a Filial", opcao "Manter Filial
   * da Demanda" = undefined/null): quando informado, a API agrupa TODOS os itens sob essa unica
   * filial em vez da filial original de cada item.
   */
  async originarDoSintetico(
    orderName: string | null,
    idFilial?: number | null
  ): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity[]>> {
    return new RestUseCase(`${this.baseEndpoint}/originar-sintetico`).Post_<FluxoAprovacaoPedidoEntity[]>({
      data: { orderName, idFilial: idFilial ?? undefined },
    });
  }

  /**
   * Origina os pedidos a partir de uma intenção de compra específica de Campanhas (FU02).
   * Espelha o gatilho legado (solicitar-compra por idIntencaoCompra): a API lê os itens da intenção,
   * agrupa por filial e cria um pedido por filial já enviado para aprovação. Retorna os originados.
   */
  async originarDeCampanhas(
    idIntencaoCompra: number,
    orderName: string | null = null
  ): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity[]>> {
    return new RestUseCase(`${this.baseEndpoint}/originar-campanhas`).Post_<FluxoAprovacaoPedidoEntity[]>({
      data: { idIntencaoCompra, orderName },
    });
  }

  /**
   * Lista os pedidos do novo fluxo visíveis ao usuário: quem pode aprovar vê todos
   * os da empresa; os demais veem apenas os próprios (visibilidade resolvida no backend).
   */
  async listarPedidos(): Promise<ServiceOperationResult<PedidoResumo[]>> {
    return new RestUseCase(this.baseEndpoint).Get_<PedidoResumo[]>();
  }

  /** RF01: envio conta como aprovacao do operador da versao atual. */
  async enviarParaAprovacao(idPedido: number): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/enviar-aprovacao`).Post_<FluxoAprovacaoPedidoEntity>({ data: {} });
  }

  /** Geracao definitiva; a API bloqueia com HTTP 409 ate a aprovacao conjunta. */
  async gerarPedido(idPedido: number): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/gerar`).Post_<FluxoAprovacaoPedidoEntity>({ data: {} });
  }

  /**
   * Carrega o detalhe do pedido (status, versao, geracaoLiberada, se o usuario pode aprovar
   * e os itens da versao atual) para montar a grade e decidir as acoes da tela.
   */
  async carregarDetalhe(idPedido: number): Promise<ServiceOperationResult<PedidoDetalhe>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}`).Get_<PedidoDetalhe>();
  }

  /**
   * Conteudo do CSV do pedido aprovado, montado no backend no mesmo layout do pedido do
   * Analitico/Sintetico (tamanhos de empresa/filial/fabricante vem da configuracao da empresa).
   */
  async exportarCsv(idPedido: number): Promise<ServiceOperationResult<string>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/csv`).Get_<string>();
  }

  /**
   * Busca itens disponiveis para o typeahead da edicao. O backend resolve a filial
   * pelo proprio pedido (so enviamos idPedido e o termo digitado), respeitando a filial correta.
   */
  async buscarItens(idPedido: number, termo: string): Promise<ServiceOperationResult<ItemBusca[]>> {
    const url = `${this.baseEndpoint}/${idPedido}/itens?termo=${encodeURIComponent(termo)}`;
    return new RestUseCase(url).Get_<ItemBusca[]>();
  }

  /**
   * Carrega os detalhes de uma versao especifica do pedido:
   * numero, data de criacao, autor e itens com valores e status.
   */
  async carregarDetalheVersao(idPedido: number, numeroVersao: number): Promise<ServiceOperationResult<PedidoVersaoDetalhe>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/versao/${numeroVersao}`).Get_<PedidoVersaoDetalhe>();
  }

  /**
   * Carrega TODAS as versoes do pedido, da mais antiga para a mais recente, cada uma com a sua
   * propria listagem de itens. A modal de versoes exibe uma listagem por versao,
   * permitindo comparar as quantidades/valores de cada uma.
   */
  async carregarVersoes(idPedido: number): Promise<ServiceOperationResult<PedidoVersaoDetalhe[]>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/versoes`).Get_<PedidoVersaoDetalhe[]>();
  }

  /** RF08: gestor (aprovador vinculado à loja do pedido) aprova a versao atual; justificativa opcional. */
  async aprovar(idPedido: number, justificativa?: string): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/aprovar`).Post_<FluxoAprovacaoPedidoEntity>({
      data: { justificativa },
    });
  }

  /** RF08: operador valida a versao retornada; justificativa opcional. */
  async validar(idPedido: number, justificativa?: string): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/validar`).Post_<FluxoAprovacaoPedidoEntity>({
      data: { justificativa },
    });
  }

  /** RF16: somente o aprovador vinculado à loja reprova; o pedido reprovado e terminal; justificativa opcional. */
  async reprovar(idPedido: number, justificativa?: string): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/reprovar`).Post_<FluxoAprovacaoPedidoEntity>({
      data: { justificativa },
    });
  }

  /**
   * Acao "Editar" (§4.5 / RN12): NAO substitui mais a versao. Envia solicitacoes
   * item-a-item (REMOCAO/ADICAO/ALTERACAO_QTD) e a justificativa (obrigatoria); o pedido volta
   * para a contraparte decidir. A parte autora e resolvida no backend pelo vinculo do usuario com a loja do pedido. A API
   * bloqueia com HTTP 409 se nao for a vez da parte ou se a solicitacao for identica a uma existente.
   */
  async editarPedido(
    idPedido: number,
    solicitacoes: SolicitacaoItem[],
    justificativa: string
  ): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/editar`).Post_<FluxoAprovacaoPedidoEntity>({
      data: { solicitacoes, justificativa },
    });
  }

  /**
   * Decide as solicitacoes pendentes (§4.5 / RN17): aceita/recusa individual ou em lote.
   * As ACEITAS geram nova versao; as RECUSADAS voltam como pendentes para a contraparte.
   */
  async decidirSolicitacoes(
    idPedido: number,
    idsAceitas: number[],
    idsRecusadas: number[],
    justificativa?: string
  ): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(`${this.baseEndpoint}/${idPedido}/decidir-solicitacoes`).Post_<FluxoAprovacaoPedidoEntity>({
      data: { idsAceitas, idsRecusadas, justificativa },
    });
  }

  /** Retira uma solicitacao pendente da propria autoria (§4.5 / RN18). */
  async retirarSolicitacao(
    idPedido: number,
    idSolicitacao: number
  ): Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>> {
    return new RestUseCase(
      `${this.baseEndpoint}/${idPedido}/solicitacoes/${idSolicitacao}/retirar`
    ).Post_<FluxoAprovacaoPedidoEntity>({ data: {} });
  }

  async consultarAprovadores(
    filtro: ConsultaAprovadorFiltro,
    page: number,
    size: number
  ): Promise<{ statusCode: number; body: ConsultaAprovador[] | null; totalCount: number }> {
    const url = `${this.baseEndpoint}/aprovadores/consulta${queryStringConsultaAprovadores(filtro, page, size)}`;
    const resposta = await new RestUseCase(url).Get();
    const totalCount = Number(resposta.headers?.['total-count'] ?? resposta.headers?.['Total-Count'] ?? 0);
    return {
      statusCode: resposta.statusCode,
      body: Array.isArray(resposta.body) ? resposta.body : null,
      totalCount: Number.isFinite(totalCount) ? totalCount : 0,
    };
  }

  async listarFiliaisConsultaAprovadores(): Promise<{ statusCode: number; body: OpcaoConsultaAprovador[] | null }> {
    const resposta = await new RestUseCase(`${this.baseEndpoint}/aprovadores/filiais`).Get();
    return { statusCode: resposta.statusCode, body: Array.isArray(resposta.body) ? resposta.body : null };
  }

  async listarAprovadoresConsulta(): Promise<{ statusCode: number; body: OpcaoConsultaAprovador[] | null }> {
    const resposta = await new RestUseCase(`${this.baseEndpoint}/aprovadores/lista`).Get();
    return { statusCode: resposta.statusCode, body: Array.isArray(resposta.body) ? resposta.body : null };
  }
}

/** Opcao (id/nome) das listas usadas nos filtros de loja e aprovador da consulta. Espelha VinculoCompraOpcaoDTO. */
export interface OpcaoConsultaAprovador {
  id: number;
  nome: string;
}
